import { computed, inject, Injectable, signal } from "@angular/core";
import { StorageRepositoryService } from "./storage.repository.service";
import { Filter } from "../../domain/interfaces/Filter.interface";
import { INITIAL_FILTER_VALUES } from "../../domain/constants/filter.constant";

@Injectable({
  providedIn: 'root'
})
export class FilterStore {
  private readonly storageService = inject(StorageRepositoryService);
  private readonly FILTER_STORAGE_KEY = 'filter';

  // State signals
  private readonly _filter = signal<Filter>(this.getInitialFilter());
  private readonly _isFilterApplied = computed(() => {
    const filter = this._filter();
    return Object.keys(filter).length > 0;
  });

  // Public computed values
  filter = computed(() => this._filter());
  readonly isFilterApplied = this._isFilterApplied;

  // Filter options
  private readonly _states = signal<string[]>([]);
  private readonly _municipalities = signal<string[]>([]);
  private readonly _indigenousLands = signal<string[]>([]);
  private readonly _conservationUnits = signal<string[]>([]);
  private readonly _status = signal<string[]>([]);
  private readonly _maintenance = signal<string[]>([]);
  private readonly _contractType = signal<string[]>([]);
  private readonly _connectionType = signal<string[]>([]);
  private readonly _partners = signal<string[]>([]);
  private readonly _specialTerritories = signal<string[]>([]);
  private readonly _energyKitModel = signal<string[]>([]);
  private readonly _dateStart = signal<Date>(new Date());
  private readonly _dateEnd = signal<Date>(new Date());
  private readonly _declaredTerritory = signal<string[]>([]);
  private readonly _workGroups = signal<string[]>([]);
  private readonly _association = signal<string[]>([]);
  private readonly _routerModel = signal<string[]>([]);

  // Public computed values
  readonly states = this._states.asReadonly();
  readonly municipalities = this._municipalities.asReadonly();
  readonly indigenousLands = this._indigenousLands.asReadonly();
  readonly conservationUnits = this._conservationUnits.asReadonly();
  readonly status = this._status.asReadonly();
  readonly maintenance = this._maintenance.asReadonly();
  readonly contractType = this._contractType.asReadonly();
  readonly connectionType = this._connectionType.asReadonly();
  readonly partners = this._partners.asReadonly();
  readonly specialTerritories = this._specialTerritories.asReadonly();
  readonly energyKitModel = this._energyKitModel.asReadonly();
  readonly dateStart = this._dateStart.asReadonly();
  readonly dateEnd = this._dateEnd.asReadonly();
  readonly declaredTerritory = this._declaredTerritory.asReadonly();
  readonly workGroups = this._workGroups.asReadonly();
  readonly association = this._association.asReadonly();
  readonly routerModel = this._routerModel.asReadonly();

  // State update methods
  setFilter(filter: Filter): void {
    const cleanedFilter = this.cleanFilter(filter);
    this._filter.set(cleanedFilter);
    this.persistFilter(cleanedFilter);
  }

  updateFilter(partialFilter: Partial<Filter>): void {
    this._filter.update(current => {
      const updatedFilter = {
        ...current,
        ...partialFilter
      };
      this.persistFilter(updatedFilter);
      return updatedFilter;
    });
  }

  resetFilter(): void {
    this._filter.set(INITIAL_FILTER_VALUES);
  }

  // Filter options update methods
  updateStates(options: string[]): void {
    this._states.set(options);
  }

  updateMunicipalities(options: string[]): void {
    this._municipalities.set(options);
  }

  updateIndigenousLands(options: string[]): void {
    this._indigenousLands.set(options);
  }

  updateConservationUnits(options: string[]): void {
    this._conservationUnits.set(options);
  }

  updateStatus(options: string[]): void {
    this._status.set(options);
  }

  updateMaintenance(options: string[]): void {
    this._maintenance.set(options);
  }

  updateContractType(options: string[]): void {
    this._contractType.set(options);
  }

  updateConnectionType(options: string[]): void {
    this._connectionType.set(options);
  }

  updatePartners(options: string[]): void {
    this._partners.set(options);
  }

  updateSpecialTerritories(options: string[]): void {
    this._specialTerritories.set(options);
  }

  updateEnergyKitModel(options: string[]): void {
    this._energyKitModel.set(options);
  }

  updateDeclaredTerritory(options: string[]): void {
    this._declaredTerritory.set(options);
  }

  updateWorkGroups(options: string[]): void {
    this._workGroups.set(options);
  }

  updateAssociation(options: string[]): void {
    this._association.set(options);
  }

  updateRouterModel(options: string[]): void {
    this._routerModel.set(options);
  }

  // Utility methods
  cleanFilter(filter: Filter): Filter {
    const cleanedFilter = { ...filter };
    Object.keys(cleanedFilter).forEach(key => {
      const value = cleanedFilter[key as keyof Filter];
      if (value === undefined || value === null || (Array.isArray(value) && value.length === 0)) {
        delete cleanedFilter[key as keyof Filter];
      }
    });
    return cleanedFilter as Filter;
  }

  private getInitialFilter(): Filter {
    const persistedFilter = this.storageService.load(this.FILTER_STORAGE_KEY, 'local') as Filter;

    if (persistedFilter) {
      // Convert date strings back to Date objects
      return {
        ...persistedFilter,
        dateStart: persistedFilter.dateStart ? new Date(persistedFilter.dateStart) : undefined,
        dateEnd: persistedFilter.dateEnd ? new Date(persistedFilter.dateEnd) : undefined
      };
    }

    return INITIAL_FILTER_VALUES;
  }

  private persistFilter(filter: Filter): void {
    this.storageService.save(this.FILTER_STORAGE_KEY, filter, 'local');
  }
}
